/**
 * SYND-15 — what a save POINTS AT, as one type the whole app shares.
 *
 * `public.saved_properties` is polymorphic as of the 20260820100000 migration:
 * every row carries exactly one of `listing_id` / `syndicated_id`, enforced by
 * `saved_properties_exactly_one_target`. This module is the client-side half of
 * that shape — the discriminated union, the column it maps to, and the cache key.
 *
 * ITS OWN MODULE, AND FREE OF ANY IMPORT THAT TOUCHES `import.meta.env`, for
 * the same reason lib/api/syndicatedQuery.ts and lib/api/searchListingsQuery.ts
 * are: the test runner here is plain `npx tsx`, lib/api/client.ts evaluates Vite
 * env at import time and dies under Node, and this type is needed by
 * src/components/home/listingView.ts — which renders inside the Vike SSR tree,
 * where lib/api/client.ts does not belong either.
 *
 * WHY A KEY AND NOT AN ID
 *   `listings.id` and `syndicated_listings.id` are both uuids from different
 *   tables and nothing guarantees they never collide. Any map, set or React key
 *   over saved items is therefore keyed by `savedTargetKey`, never by the bare
 *   uuid — otherwise one coincidental collision makes a marketplace listing and
 *   an indexed entry share a heart, and each one's save silently toggles the
 *   other's. The same rule, for the same reason, as `unifiedResultKey` in
 *   pages/Home.tsx (SYND-06).
 */

/** Which table the id belongs to. The discriminant, everywhere. */
export type SavedTargetKind = 'listing' | 'syndicated';

/** One thing a user can save. Exactly one kind, exactly one id. */
export interface SavedTarget {
  kind: SavedTargetKind;
  id: string;
}

/** A marketplace listing. */
export const savedListing = (id: string): SavedTarget => ({ kind: 'listing', id });

/** An indexed entry from `syndicated_public`. */
export const savedSyndicated = (id: string): SavedTarget => ({ kind: 'syndicated', id });

/**
 * The cache key. `listing:<uuid>` / `syndicated:<uuid>`.
 *
 * Never the bare id — see the header. Every Set/Map of saved items in this repo
 * is keyed by this function, and the only way to look one up is to have a
 * `SavedTarget` in hand, which is what makes a kind-less lookup a type error
 * rather than a silent collision.
 */
export const savedTargetKey = (target: SavedTarget): string => `${target.kind}:${target.id}`;

/** The `saved_properties` column this kind writes to. The other stays null. */
export const savedTargetColumn = (kind: SavedTargetKind): 'listing_id' | 'syndicated_id' =>
  kind === 'syndicated' ? 'syndicated_id' : 'listing_id';

/**
 * The row shape `saved_properties` returns, and the target it means.
 *
 * `null` for a row carrying neither — impossible under the CHECK constraint, and
 * handled anyway rather than trusted: this function runs on data from over the
 * wire, and a row that cannot be interpreted is dropped from the list instead of
 * becoming a `{ kind: 'listing', id: undefined }` that would silently DELETE by
 * `listing_id=eq.undefined` the first time someone unsaved it.
 *
 * `listing_id` wins if both are somehow set, which is the pre-SYND-15 reading
 * and the conservative one.
 */
export function savedTargetFromRow(row: {
  listing_id?: string | null;
  syndicated_id?: string | null;
}): SavedTarget | null {
  if (row.listing_id) return savedListing(row.listing_id);
  if (row.syndicated_id) return savedSyndicated(row.syndicated_id);
  return null;
}
